import React, { useState, useEffect, useCallback, useMemo } from 'react';
import EventFormModal from './EventFormModal'; // Add this at the top
import moment from 'moment';
import PropTypes from 'prop-types';
import ConfirmModal from './components/ConfirmModal';
import ErrorModal from './components/ErrorModal';
import MobileNavigation from './components/MobileNavigation';
import MobileFooter from './components/MobileFooter';
import SeriesModal from './components/SeriesModal';
import CustomDateRangeModal from './components/CustomDateRangeModal';
import ListView from './components/ListView';
import ThreeDayView from './components/ThreeDayView';
import {
  Calendar,
  DateLocalizer,
  momentLocalizer,
} from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { useRef, useLayoutEffect } from 'react';
import './App.css';

const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

moment.locale('en', {
  week: {
    dow: 1, // Start week on Monday
  },
  longDateFormat: {
    LT: 'HH:mm',
    LTS: 'HH:mm:ss',
  }
});

// 🛡️ Series Creation State Management (localStorage)
const SERIES_STORAGE_KEY = 'mw_series_creating';

const getSeriesCreationState = () => {
  try {
    const state = localStorage.getItem(SERIES_STORAGE_KEY);
    return state ? JSON.parse(state) : null;
  } catch (error) {
    console.error('Error reading series creation state:', error);
    return null;
  }
};

const setSeriesCreationState = (state) => {
  try {
    if (state) {
      localStorage.setItem(SERIES_STORAGE_KEY, JSON.stringify(state));
    } else {
      localStorage.removeItem(SERIES_STORAGE_KEY);
    }
  } catch (error) {
    console.error('Error saving series creation state:', error);
  }
};

const addCreatedEventToSeries = (logId, projectId, timecodeId) => {
  const state = getSeriesCreationState();
  if (state) {
    state.createdEvents.push({ logId, projectId, timecodeId, createdAt: Date.now() });
    setSeriesCreationState(state);
  }
};

// Helper function to format decimal hours to HH:MM format
const formatHours = (decimalHours) => {
  const isNegative = decimalHours < 0;
  const absHours = Math.abs(decimalHours);
  const hours = Math.floor(absHours);
  const minutes = Math.round((absHours - hours) * 60);
  const sign = isNegative ? '-' : '';
  return `${sign}${hours}:${minutes.toString().padStart(2, '0')}h`;
};

function App() {
  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]); // State to store fetched projects
  const [currentView, setCurrentView] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [error, setError] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [slotInfo, setSlotInfo] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [eventPendingDelete, setEventPendingDelete] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState({ title: '', message: '' });
  const getModalPositionStyle = (date, view) => {
    const day = moment(date).day();
    let positionStyle = { right: '20px' };
    if (view === 'week') {
      // Sunday (0), Thursday (4), Friday (5), Saturday (6) => left
      if (day === 0 || day >= 4) positionStyle = { left: '20px' };
    } else if (view === 'work_week') {
      // Wednesday (3), Thursday (4), Friday (5) => left
      if (day >= 3) positionStyle = { left: '20px' };
    } else if (view === 'three_day') {
      // For 3-day view, determine position based on ThreeDayView logic
      // ThreeDayView shows 3 consecutive days starting from currentDate
      // So we need to know the actual 3-day range the calendar will show
      
      const eventDate = moment(date).startOf('day');
      
      // Calculate the 3-day range that would contain this event date
      // The range starts from the date that makes this event fall within it
      let rangeStart;
      
      // If we have currentDate, use it as reference for the current range
      if (currentDate) {
        rangeStart = moment(currentDate).startOf('day');
      } else {
        // Fallback: assume event date is the start of a 3-day range
        rangeStart = eventDate;
      }
      
      // Calculate position of event within the 3-day range (0, 1, or 2)
      const dayIndex = eventDate.diff(rangeStart, 'days');
      
      // If on the last day (index 2) of 3-day view, position modal on left
      // Also handle cases where dayIndex might be outside 0-2 range due to navigation
      if (dayIndex === 2 || dayIndex < 0 || dayIndex > 2) {
        positionStyle = { left: '20px' };
      }
    }
    return {
      ...positionStyle,
      position: 'fixed',
      top: '80px',
      zIndex: 100,
    };
  };
  const [modalPosition, setModalPosition] = useState(getModalPositionStyle(new Date(), currentView));
  const [isDragging, setIsDragging] = useState(false);

  // Show error in modal instead of taking over the page
  const showError = useCallback((title, message) => {
    setErrorModalData({ title, message });
    setShowErrorModal(true);
  }, []);

  const [eventToCopy, setEventToCopy] = useState(null);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, event: null });
  const contextMenuRef = useRef(null);
  const [slotContextMenu, setSlotContextMenu] = useState({ visible: false, x: 0, y: 0, slot: null });
  const slotContextMenuRef = useRef(null);
  const [originalEventBeforeEdit, setOriginalEventBeforeEdit] = useState(null);
  const [selectedSlotDate, setSelectedSlotDate] = useState(null);
  
  // Series Modal state
  const [showSeriesModal, setShowSeriesModal] = useState(false);
  const [seriesTemplateEvent, setSeriesTemplateEvent] = useState(null);
  
  // Series Delete state
  const [showSeriesDeleteConfirm, setShowSeriesDeleteConfirm] = useState(false);
  const [seriesPendingDelete, setSeriesPendingDelete] = useState({ seriesId: null, events: [] });
  const [isDeletingSeries, setIsDeletingSeries] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState({ current: 0, total: 0 });

  // 🛡️ Series Recovery state  
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryData, setRecoveryData] = useState(null);
  const [isRecovering, setIsRecovering] = useState(false);

  // Custom Date Range state
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customDateRange, setCustomDateRange] = useState(null);
  const [customRangeData, setCustomRangeData] = useState(null);
  const [isLoadingCustomRange, setIsLoadingCustomRange] = useState(false);

  // 🛡️ Recovery System - Check for incomplete series on app start
  useEffect(() => {
    const checkForIncompleteCreation = () => {
      const incompleteState = getSeriesCreationState();
      if (incompleteState) {
        const timeElapsed = Date.now() - incompleteState.startTime;
        const hoursElapsed = timeElapsed / (1000 * 60 * 60);
        
        console.log(`🔍 Found incomplete series creation: ${incompleteState.createdEvents.length}/${incompleteState.totalEvents} events created`);
        
        setRecoveryData({
          ...incompleteState,
          hoursElapsed: Math.round(hoursElapsed * 10) / 10
        });
        setShowRecoveryModal(true);
      }
    };

    // Check after a short delay to ensure app is loaded
    const timer = setTimeout(checkForIncompleteCreation, 1000);
    return () => clearTimeout(timer);
  }, []);

  // 🛡️ BeforeUnload Protection - Warn user about incomplete series
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const incompleteState = getSeriesCreationState();
      if (incompleteState) {
        e.preventDefault();
        e.returnValue = 'You are currently creating a series. If you leave this page, all already created events will be automatically deleted.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // 🛡️ Handle recovery modal actions
  const handleRecoveryCleanup = useCallback(async () => {
    setIsRecovering(true);
    if (recoveryData?.createdEvents?.length) {
      console.log(`🔄 Cleaning up ${recoveryData.createdEvents.length} created events...`);
      
      for (const eventInfo of recoveryData.createdEvents) {
        try {
          const zuid = new URLSearchParams(window.location.search).get('zuid');
          await fetch(`${process.env.REACT_APP_API_BASE_URL}/delete-timelog`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              zuid, 
              logId: eventInfo.logId, 
              projectId: eventInfo.projectId, 
              timecodeId: eventInfo.timecodeId 
            }),
          });
          
          // Remove from UI state
          setEvents(prev => prev.filter(ev => ev.logId !== eventInfo.logId));
        } catch (error) {
          console.error('Error cleaning up event:', eventInfo.logId, error);
        }
      }
      
      // Clear localStorage
      setSeriesCreationState(null);
      console.log('✅ Recovery cleanup completed');
    }
    setShowRecoveryModal(false);
    setRecoveryData(null);
    setIsRecovering(false);
  }, [recoveryData]);

  const handleRecoveryDismiss = useCallback(() => {
    // Clear localStorage without cleanup - user chooses to keep partial series
    setSeriesCreationState(null);
    setShowRecoveryModal(false);
    setRecoveryData(null);
  }, []);

  // Handle Create Series functionality (from right-click)
  const handleCreateSeries = useCallback((templateEvent) => {
    setSeriesTemplateEvent(templateEvent);
    setShowSeriesModal(true);
    setContextMenu({ visible: false, x: 0, y: 0, event: null });
  }, []);

  // Handle Create Series from Header (without template)
  const handleCreateSeriesFromHeader = useCallback(() => {
    // Create a default template event for new series
    const defaultTemplate = {
      title: '',
      projectId: '',
      timecodeId: '',
      billingtype: 'Billable',
      projectName: '',
      timecodeName: '',
      approval_status: 'Pending',
      start: new Date(),
      end: new Date(),
    };
    setSeriesTemplateEvent(defaultTemplate);
    setShowSeriesModal(true);
  }, []);

  // Handle Custom Date Range
  const handleCustomDateRangeClick = useCallback(() => {
    setShowCustomDateModal(true);
  }, []);

  const handleCustomDateRangeApply = useCallback((range) => {
    setIsLoadingCustomRange(true);
    setCustomDateRange(range);
    
    // Calculate the data for the custom range
    try {
      if (range.compareWithPreviousYear) {
        const comparisonData = getYearOverYearComparison(range.start, range.end);
        setCustomRangeData({
          ...comparisonData,
          range,
          label: range.label || 'Custom Range'
        });
      } else {
        const currentPeriod = getCustomPeriodTotals(range.start, range.end);
        setCustomRangeData({
          current: currentPeriod,
          range,
          label: range.label || 'Custom Range',
          hasData: currentPeriod.total > 0
        });
      }
    } catch (error) {
      console.error('Error calculating custom range data:', error);
      showError('Calculation Error', 'An error occurred while calculating the selected period');
    } finally {
      setIsLoadingCustomRange(false);
    }
  }, [events, showError]);

  const handleCustomDateRangeClose = useCallback(() => {
    setShowCustomDateModal(false);
  }, []);

  const handleClearCustomRange = useCallback(() => {
    setCustomDateRange(null);
    setCustomRangeData(null);
  }, []);

  // Helper function for long press on mobile
  const setupLongPress = useCallback((handler, contextData = null) => ({
    onTouchStart: (e) => {
      const touchTimer = setTimeout(() => {
        const syntheticEvent = {
          preventDefault: () => e.preventDefault(),
          clientX: e.touches[0].clientX,
          clientY: e.touches[0].clientY,
          target: e.target,
          currentTarget: e.currentTarget
        };
        handler(contextData || e, syntheticEvent);
      }, 500); // 500ms long press
      
      e.currentTarget._touchTimer = touchTimer;
    },
    onTouchEnd: (e) => {
      if (e.currentTarget._touchTimer) {
        clearTimeout(e.currentTarget._touchTimer);
        e.currentTarget._touchTimer = null;
      }
    },
    onTouchMove: (e) => {
      if (e.currentTarget._touchTimer) {
        clearTimeout(e.currentTarget._touchTimer);
        e.currentTarget._touchTimer = null;
      }
    }
  }), []);

  // Handle Delete Entire Series functionality
  const handleDeleteSeries = useCallback((seriesId) => {
    if (isDeletingSeries) return; // Don't allow new deletions while deleting
    
    const seriesEvents = events.filter(event => event.seriesId === seriesId);
    if (seriesEvents.length > 0) {
      setSeriesPendingDelete({ seriesId, events: seriesEvents });
      setShowSeriesDeleteConfirm(true);
      setContextMenu({ visible: false, x: 0, y: 0, event: null });
    }
  }, [events, isDeletingSeries]);

  // Confirm and execute series deletion
  const handleConfirmDeleteSeries = useCallback(async () => {
    setShowSeriesDeleteConfirm(false);
    setIsDeletingSeries(true);
    setDeleteProgress({ current: 0, total: seriesPendingDelete.events.length });
    
    try {
      let deletedCount = 0;
      
      // Delete all events in the series
      for (const event of seriesPendingDelete.events) {
        if (event.logId && event.projectId && event.timecodeId) {
          await new Promise((resolve, reject) => {
            const zuid = new URLSearchParams(window.location.search).get('zuid');

            fetch(`${process.env.REACT_APP_API_BASE_URL}/delete-timelog`, {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ zuid, logId: event.logId, projectId: event.projectId, timecodeId: event.timecodeId }),
            })
              .then((res) => res.json())
              .then((data) => {
                if (data.success) {
                  deletedCount++;
                  setDeleteProgress({ current: deletedCount, total: seriesPendingDelete.events.length });
                  resolve(data);
                } else {
                  reject(new Error('Failed to delete event'));
                }
              })
              .catch(reject);
          });
        } else {
          // Count non-API events too
          deletedCount++;
          setDeleteProgress({ current: deletedCount, total: seriesPendingDelete.events.length });
        }
      }

      // Remove events from state
      setEvents(prev => prev.filter(event => event.seriesId !== seriesPendingDelete.seriesId));
      
      // Reset state
      setSeriesPendingDelete({ seriesId: null, events: [] });
      setIsDeletingSeries(false);
      setDeleteProgress({ current: 0, total: 0 });
      
    } catch (error) {
      console.error('Error deleting series:', error);
      setIsDeletingSeries(false);
      setDeleteProgress({ current: 0, total: 0 });
      showError('Delete Failed', 'Failed to delete some events in the series. Please try again.');
    }
  }, [seriesPendingDelete, showError]);

  // Handle batch creation of series events
  const handleBatchCreateSeries = useCallback(async (eventsToCreate, setProgress) => {
    const seriesId = eventsToCreate[0]?.seriesId;
    if (!seriesId) {
      throw new Error('Series ID is required');
    }

    // 🛡️ Initialize series creation state in localStorage
    const seriesState = {
      seriesId,
      totalEvents: eventsToCreate.length,
      createdEvents: [],
      startTime: Date.now(),
      templateData: {
        projectId: eventsToCreate[0]?.projectId,
        timecodeId: eventsToCreate[0]?.timecodeId,
        title: eventsToCreate[0]?.title,
        billingtype: eventsToCreate[0]?.billingtype,
      }
    };
    setSeriesCreationState(seriesState);

    try {
      for (let i = 0; i < eventsToCreate.length; i++) {
        const eventData = eventsToCreate[i];
        setProgress({ current: i, total: eventsToCreate.length });
        
        // Create each event using existing handleSaveEvent logic
        await new Promise((resolve, reject) => {
          const zuid = new URLSearchParams(window.location.search).get('zuid');

          fetch(`${process.env.REACT_APP_API_BASE_URL}/send-timelog`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              zuid: zuid,
              start: eventData.start,
              end: eventData.end,
              projectId: eventData.projectId,
              timecodeId: eventData.timecodeId,
              notes: eventData.title,
              bill_status: eventData.billingtype,
              date: eventData.start.toISOString().split('T')[0],
              sendToCapLegal: eventData.sendToCapLegal,
            }),
          })
            .then((res) => res.json())
            .then((data) => {
              if (data.success) {
                const newEventWithId = {
                  title: eventData.title,
                  start: eventData.start,
                  end: eventData.end,
                  projectId: eventData.projectId,
                  timecodeId: eventData.timecodeId,
                  projectName: eventData.projectName || '',
                  timecodeName: eventData.timecodeName || '',
                  billingtype: eventData.billingtype || '',
                  approval_status: 'Pending',
                  logId: data.data.id,
                  seriesId: eventData.seriesId, // 🏷️ Preserve series ID
                };

                setEvents((prev) => [...prev, newEventWithId]);
                
                // 🛡️ Track created event in localStorage
                addCreatedEventToSeries(data.data.id, eventData.projectId, eventData.timecodeId);
                
                resolve(data);
              } else {
                reject(new Error('Failed to save event'));
              }
            })
            .catch(reject);
        });

        // Small delay to prevent overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      setProgress({ current: eventsToCreate.length, total: eventsToCreate.length });
      
      // 🛡️ Series completed successfully - clear localStorage
      setSeriesCreationState(null);
      console.log('✅ Series creation completed successfully');
      
      // Refetch projects to update recent usage - call directly without dependency
      const zuid = new URLSearchParams(window.location.search).get('zuid');
      if (zuid) {
        fetch(`${process.env.REACT_APP_API_BASE_URL}/projects?zuid=${zuid}`)
          .then((res) => res.json())
          .then((projectData) => {
            if (projectData.success) {
              setProjects(projectData.data);
            }
          })
          .catch(() => {
            // Silently fail - not critical for series creation
          });
      }
      
    } catch (error) {
      console.error('🚨 Error creating series:', error);
      
      // 🛡️ Get current state and rollback created events
      const currentState = getSeriesCreationState();
      if (currentState?.createdEvents?.length) {
        console.log(`🔄 Rolling back series creation (${currentState.createdEvents.length} events created)`);
        
        for (const eventInfo of currentState.createdEvents) {
          try {
            const zuid = new URLSearchParams(window.location.search).get('zuid');
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/delete-timelog`, {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ 
                zuid, 
                logId: eventInfo.logId, 
                projectId: eventInfo.projectId, 
                timecodeId: eventInfo.timecodeId 
              }),
            });
            
            // Remove from UI state
            setEvents(prev => prev.filter(ev => ev.logId !== eventInfo.logId));
          } catch (error) {
            console.error('Error rolling back event:', eventInfo.logId, error);
          }
        }
        
        // Clear localStorage
        setSeriesCreationState(null);
        console.log('✅ Series rollback completed');
      }
      
      throw error;
    }
  }, []);

  // Handle Add New functionality
  const handleAddNew = useCallback(() => {
    // 1. Round up to next 30-minute interval (hel/halv timer)
    const now = (currentView === 'day' || currentView === 'three_day') ? new Date(currentDate) : new Date();
    const minutes = now.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 30) * 30;
    if (roundedMinutes === 60) {
      now.setHours(now.getHours() + 1);
      now.setMinutes(0, 0, 0);
    } else {
      now.setMinutes(roundedMinutes, 0, 0);
    }
    const start = new Date(now);
    const end = new Date(start.getTime() + 60 * 60 * 1000); // +1 hour

    // 2. Create a marked event
    const tempId = `temp-${Date.now()}`;
    const placeholder = {
      title: '',
      start,
      end,
      logId: tempId,
      approval_status: 'Marked',
      projectId: '',
      timecodeId: '',
      billingtype: '',
      projectName: '',
      timecodeName: '',
    };
    setEvents(prev => [
      ...prev.filter(ev => ev.approval_status !== 'Marked' && !(ev.logId && ev.logId.startsWith('temp-'))),
      placeholder
    ]);
    setSlotInfo({ start, end, tempId });
    setModalPosition(getModalPositionStyle(start, currentView));
    setShowModal(true);
  }, [currentView, currentDate, setEvents, setSlotInfo, setModalPosition, setShowModal, getModalPositionStyle]);

  // useEffect(() => {
  //   const handleContextMenu = (e) => {
  //     e.preventDefault();
  //   };

  //   document.addEventListener('contextmenu', handleContextMenu);

  //   return () => {
  //     document.removeEventListener('contextmenu', handleContextMenu);
  //   };
  // }, []);

  const handleRightClickEvent = useCallback((event, e) => {
    if (event.approval_status === 'Marked') return; // Only block Marked events

    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      event,
    });
  }, []);

  const handleRightClickSlot = useCallback((slotInfo, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Close any existing context menus
    setContextMenu({ visible: false, x: 0, y: 0, event: null });
    
    setSlotContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      slot: slotInfo,
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only trigger if not typing in an input or textarea and modal is not open
      if (
        e.key === 'n' &&
        !showModal &&
        document.activeElement.tagName !== 'INPUT' &&
        document.activeElement.tagName !== 'TEXTAREA'
      ) {
        // 1. Round up to next 30-minute interval (hel/halv timer)
        const now = new Date();
        const minutes = now.getMinutes();
        const roundedMinutes = Math.ceil(minutes / 30) * 30;
        if (roundedMinutes === 60) {
          now.setHours(now.getHours() + 1);
          now.setMinutes(0, 0, 0);
        } else {
          now.setMinutes(roundedMinutes, 0, 0);
        }
        const start = new Date(now);
        const end = new Date(start.getTime() + 60 * 60 * 1000); // +1 hour

        // 2. Create a marked event
        const tempId = `temp-${Date.now()}`;
        const placeholder = {
          title: '',
          start,
          end,
          logId: tempId,
          approval_status: 'Marked',
          projectId: '',
          timecodeId: '',
          billingtype: '',
          projectName: '',
          timecodeName: '',
        };
        setEvents(prev => [
          ...prev.filter(ev => ev.approval_status !== 'Marked' && !(ev.logId && ev.logId.startsWith('temp-'))),
          placeholder
        ]);
        setSlotInfo({ start, end, tempId });
        setModalPosition(getModalPositionStyle(start, currentView));
        setShowModal(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showModal, currentView, getModalPositionStyle, setEvents, setSlotInfo, setModalPosition, setShowModal]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(event.target)
      ) {
        setContextMenu({ visible: false, x: 0, y: 0, event: null });
      }
      
      if (
        slotContextMenuRef.current &&
        !slotContextMenuRef.current.contains(event.target)
      ) {
        setSlotContextMenu({ visible: false, x: 0, y: 0, slot: null });
      }
    };

    if (contextMenu.visible || slotContextMenu.visible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [contextMenu.visible, slotContextMenu.visible]);


  const isDraggable = (event) => {
    return event.approval_status !== 'Approved';
  };

  const isResizable = (event) => {
    return event.approval_status !== 'Approved';
  };


  const calendarRef = useRef(null);
  const hasAutoScrolledRef = useRef(false);
  const previousViewRef = useRef(currentView);
  const previousDateRef = useRef(currentDate);
  const previousAuthRef = useRef(authenticated);

  // Reusable function to scroll calendar to specific time
  const scrollToTime = useCallback((targetTime, smooth = false) => {
    if (!calendarRef.current) return;

    const scrollContainer = calendarRef.current.querySelector('.rbc-time-content');
    if (!scrollContainer) return;

    // Extract hour from target time
    const targetHour = typeof targetTime === 'number' ? targetTime : targetTime.getHours();
    const targetMinutes = typeof targetTime === 'number' ? 0 : targetTime.getMinutes();
    
    // Calculate total minutes from start of day
    const totalMinutes = targetHour * 60 + targetMinutes;
    
    // Try to calculate actual pixels per hour from DOM
    const timeSlots = scrollContainer.querySelectorAll('.rbc-time-slot');
    let pixelsPerHour = 40; // fallback
    
    if (timeSlots.length > 0) {
      const slotHeight = timeSlots[0].offsetHeight;
      const slotsPerHour = 2; // 30-minute intervals = 2 slots per hour
      pixelsPerHour = slotHeight * slotsPerHour;
    }
    
    // Calculate scroll position - add some padding above for context
    const paddingHours = 1; // Show 1 hour above for context
    const scrollPosition = Math.max(0, (totalMinutes / 60 - paddingHours) * pixelsPerHour);
    
    console.log('Scrolling to time:', {
      targetTime: typeof targetTime === 'number' ? `${targetTime}:00` : targetTime.toTimeString(),
      targetHour,
      targetMinutes,
      totalMinutes,
      pixelsPerHour,
      scrollPosition,
      smooth
    });
    
    if (smooth) {
      scrollContainer.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
    } else {
      scrollContainer.scrollTop = scrollPosition;
    }
  }, []);

  useLayoutEffect(() => {
    if (!calendarRef.current || showModal) return; // <- skip if modal is open

    const scrollContainer = calendarRef.current.querySelector('.rbc-time-content');

    if (scrollContainer) {
      // Check if view, date, or auth status has changed
      const viewChanged = previousViewRef.current !== currentView;
      const dateChanged = previousDateRef.current !== currentDate;
      const authChanged = previousAuthRef.current !== authenticated;
      
      // Only auto-scroll on initial load or when view/date/auth changes, not when events are updated
      if (!hasAutoScrolledRef.current || viewChanged || dateChanged || authChanged) {
        // Get current time and scroll to it (with some padding above)
        const now = new Date();
        const currentHour = now.getHours();
        
        // Go back 2 hours for context, but not below 6 AM
        let targetHour = Math.max(6, currentHour - 2);
        
        // If it's early morning (before 8 AM), just start at 6 AM
        if (currentHour < 8) {
          targetHour = 6;
        }
        
        // Debug info
        const reason = !hasAutoScrolledRef.current ? 'initial load' : 
                      (viewChanged ? 'view change' : 
                       (dateChanged ? 'date change' : 'auth change'));
        console.log('Auto-scroll:', { currentHour, targetHour, time: now.toTimeString(), reason });
        
        // Use the reusable scroll function
        scrollToTime(targetHour, false);
        hasAutoScrolledRef.current = true;
      }
      
      // Always update previous values for next comparison (regardless of whether we scrolled)
      previousViewRef.current = currentView;
      previousDateRef.current = currentDate;
      previousAuthRef.current = authenticated;
    }
  }, [currentView, showModal, currentDate, scrollToTime, authenticated]); // Added 'authenticated' to ensure auto-scroll works after login


  // Check ZUID on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const zuid = params.get('zuid');

    if (!zuid) {
      setError('Error 1 - contact ZRM');
      return;
    }

    fetch(`${process.env.REACT_APP_API_BASE_URL}/login?zuid=${zuid}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setAuthenticated(true);

          // Fetch timelogs after successful login
          fetch(`${process.env.REACT_APP_API_BASE_URL}/timelogs?userid=${zuid}`)
            .then((res) => res.json())
            .then((timelogData) => {
              const allLogs = timelogData.data.flatMap(group => group.timelogs || []);
              const transformed = transformTimelogsToEvents({ timelogs: { date: allLogs } });
              setEvents(transformed);
            })

            .catch(() => {
              showError('Data Error', 'Failed to fetch timelogs. Please try refreshing the page.');
            });

          // Fetch projects after successful login
          fetch(`${process.env.REACT_APP_API_BASE_URL}/projects?zuid=${zuid}`)
            .then((res) => res.json())
            .then((projectData) => {
              if (projectData.success) {
                // Set projects in state after fetching
                console.log('Fetched projects:', projectData.data);
                setProjects(projectData.data);
              } else {
                showError('Projects Error', 'Failed to fetch projects');
              }
            })
            .catch(() => {
              showError('Connection Error', 'Failed to fetch projects. Please check your connection.');
            });

        } else {
          setError('Error 2 - contact ZRM');
        }
      })
      .catch(() => {
        setError('Error 3 - contact ZRM');
      });
  }, [showError]);

  function transformTimelogsToEvents(apiData) {
    if (!apiData || !apiData.timelogs || !apiData.timelogs.date) return [];
    if (!apiData.timelogs.date.length) return [];

    const events = apiData.timelogs.date.map((day) => {
      const date = new Date(day.date_long);

      return day.tasklogs.map((log) => {
        let start, end;

        if (log.start_time && log.end_time) {
          const startTime = log.start_time.split(':');
          const endTime = log.end_time.split(':');
          start = new Date(date);
          start.setHours(startTime[0], startTime[1]);
          end = new Date(date);
          end.setHours(endTime[0], endTime[1]);
          
          // Handle events that cross midnight
          if (end <= start) {
            end = new Date(end.getTime() + 24 * 60 * 60 * 1000); // Add 24 hours
          }
          
          // Check if duration exceeds 23 hours and cap it
          const durationHours = (end - start) / (1000 * 60 * 60);
          if (durationHours > 23) {
            console.warn('Event from API exceeds 23 hours, capping duration:', {
              logId: log.id_string,
              originalDuration: durationHours,
              start: start,
              end: end
            });
            end = new Date(start.getTime() + 23 * 60 * 60 * 1000); // Cap at 23 hours
          }
        } else {
          start = new Date(date);
          start.setHours(8, 0);
          const durationMinutes = parseInt(log.total_minutes) || 0;
          
          // Check if duration exceeds 23 hours and cap it
          if (durationMinutes > 23 * 60) {
            console.warn('Event from API (total_minutes) exceeds 23 hours, capping duration:', {
              logId: log.id_string,
              originalMinutes: durationMinutes,
              cappedMinutes: 23 * 60
            });
            end = new Date(start.getTime() + 23 * 60 * 60 * 1000); // Cap at 23 hours
          } else {
            end = new Date(start.getTime() + durationMinutes * 60000);
          }
        }

        return {
          title: log.notes,
          start,
          end,
          projectId: log.project?.id_string || '',
          timecodeId: log.task?.id_string || '',
          projectName: log.project?.name || '',
          timecodeName: log.task?.name || '',
          approval_status: log.approval_status || '',
          billingtype : log.bill_status || '',
          logId: log.id_string || '',
        };
      });
    });

    return events.flat();
  }

  const fetchProjects = useCallback(() => {
    const zuid = new URLSearchParams(window.location.search).get('zuid');
    if (!zuid) return;

    fetch(`${process.env.REACT_APP_API_BASE_URL}/projects?zuid=${zuid}`)
      .then((res) => res.json())
      .then((projectData) => {
        if (projectData.success) {
          console.log('Refetched projects:', projectData.data);
          setProjects(projectData.data);
        } else {
          showError('Projects Error', 'Failed to fetch projects');
        }
      })
      .catch(() => {
        showError('Connection Error', 'Failed to fetch projects. Please check your connection.');
      });
  }, [showError]);


  const eventPropGetter = (event) => {
    console.log('eventPropGetter called for event:', event);
    let backgroundColor = '#a9bec1'; // Default
    let borderStyle = 'solid';
    let borderColor = 'rgba(65, 60, 65, 0.4)';
    // if event projectid is 274231000012917617 set background color to blue
    if (event.projectId === '274231000012917617' || event.timecodeName === '(SYNC) Outlook Sync') {
      backgroundColor = '#d0e1f9'; // Light blue
    } else if (event.approval_status === 'Approved') {
      backgroundColor = event.billingtype === 'Billable' ? '#e8eddf' : '#e0dabb';
    } else if (event.approval_status === 'Rejected' || event.approval_status === 'Changed') {
      backgroundColor = '#767279';
    }

    // Marked (temporary) event: transparent gold, dashed border
    if (event.approval_status === 'Marked') {
      backgroundColor = 'rgba(204, 183, 120, 0.15)';
      borderStyle = 'dashed';
      borderColor = '#ccb778';
    }
    // Actively editing event: highlight with a solid border and subtle background
    else if (editingEvent?.logId === event.logId) {
      backgroundColor = '#fffbe6'; // Light yellow
      borderStyle = 'solid';
      borderColor = '#ccb778';
    }

    return {
      style: {
        backgroundColor,
        color: event.approval_status === 'Rejected' ? 'white' : 'black',
        borderRadius: '4px',
        border: `1px ${borderStyle} ${borderColor}`,
        padding: '2px 4px',
        boxShadow: event.approval_status === 'Marked'
          ? '0 0 0 2px #ccb77833'
          : '0 0 10px 0 rgba(0, 0, 0, 0.2)',
        opacity: event.approval_status === 'Marked' ? 0.95 : 1,
      },
    };
  };


  // Helper: check if event is within current view range
  const isInCurrentView = (event) => {
    if (currentView === 'work_week') {
      const weekStart = moment(currentDate).startOf('week').add(0, 'days'); // Monday
      const weekEnd = moment(currentDate).startOf('week').add(4, 'days').endOf('day'); // Friday
      return moment(event.start).isBetween(weekStart, weekEnd, null, '[]');
    }

    const viewStart = moment(currentDate).startOf(
      currentView === 'month'
        ? 'month'
        : currentView === 'week'
        ? 'week'
        : 'day'
    );
    const viewEnd = moment(currentDate).endOf(
      currentView === 'month'
        ? 'month'
        : currentView === 'week'
        ? 'week'
        : 'day'
    );
    return moment(event.start).isBetween(viewStart, viewEnd, null, '[]');
  };


  // Get totals for current view
  const totalsCurrentView = useMemo(() => {
    let billable = 0;
    let nonBillable = 0;

    events.forEach(event => {
      if (isInCurrentView(event)) {
        const duration = (new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60); // in hours
        
        // Debug negative durations
        if (duration < 0) {
          console.warn('Negative duration found:', {
            event,
            start: event.start,
            end: event.end,
            duration
          });
        }
        
        if (event.billingtype === 'Billable') {
          billable += duration;
        } else {
          nonBillable += duration;
        }
      }
    });

    return { billable, nonBillable };
  }, [events, currentDate, currentView]);

  // Get year-to-date totals
  const totalsYTD = useMemo(() => {
    let billable = 0;
    let nonBillable = 0;
    const startOfYear = moment().startOf('year');

    events.forEach(event => {
      if (moment(event.start).isSameOrAfter(startOfYear)) {
        const duration = (new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60); // in hours
        if (event.billingtype === 'Billable') {
          billable += duration;
        } else {
          nonBillable += duration;
        }
      }
    });

    return { billable, nonBillable };
  }, [events]);

  // Get custom period totals - using regular function to avoid hoisting issues
  const getCustomPeriodTotals = (startDate, endDate) => {
    let billable = 0;
    let nonBillable = 0;
    const start = moment(startDate).startOf('day');
    const end = moment(endDate).endOf('day');

    events.forEach(event => {
      if (moment(event.start).isBetween(start, end, null, '[]')) {
        const duration = (new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60); // in hours
        
        // Skip negative durations
        if (duration < 0) {
          console.warn('Skipping negative duration:', { event, duration });
          return;
        }
        
        if (event.billingtype === 'Billable') {
          billable += duration;
        } else {
          nonBillable += duration;
        }
      }
    });

    return { billable, nonBillable, total: billable + nonBillable };
  };

  // Get year-over-year comparison - using regular function to avoid hoisting issues
  const getYearOverYearComparison = (startDate, endDate) => {
    // Current period
    const currentPeriod = getCustomPeriodTotals(startDate, endDate);
    
    // Previous year same period
    const prevYearStart = moment(startDate).subtract(1, 'year');
    const prevYearEnd = moment(endDate).subtract(1, 'year');
    const previousYearPeriod = getCustomPeriodTotals(
      prevYearStart.format('YYYY-MM-DD'),
      prevYearEnd.format('YYYY-MM-DD')
    );



    // Calculate differences
    const totalDiff = currentPeriod.total - previousYearPeriod.total;
    const billableDiff = currentPeriod.billable - previousYearPeriod.billable;
    
    // Calculate percentage changes
    const totalPercentChange = previousYearPeriod.total > 0 
      ? ((totalDiff / previousYearPeriod.total) * 100) 
      : (currentPeriod.total > 0 ? 100 : 0);
    
    const billablePercentChange = previousYearPeriod.billable > 0 
      ? ((billableDiff / previousYearPeriod.billable) * 100) 
      : (currentPeriod.billable > 0 ? 100 : 0);

    return {
      current: currentPeriod,
      previous: previousYearPeriod,
      differences: {
        total: totalDiff,
        billable: billableDiff,
        totalPercent: totalPercentChange,
        billablePercent: billablePercentChange
      },
      hasData: currentPeriod.total > 0 || previousYearPeriod.total > 0
    };
  };

  // Get daily totals for current week
  const getDailyTotals = useCallback((date) => {
    const dayStart = moment(date).startOf('day');
    const dayEnd = moment(date).endOf('day');
    
    let billable = 0;
    let nonBillable = 0;
    events.forEach(event => {
      if (moment(event.start).isBetween(dayStart, dayEnd, null, '[]')) {
        const duration = (new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60);
        if (event.billingtype === 'Billable') {
          billable += duration;
        } else {
          nonBillable += duration;
        }
      }
    });
    
    return { billable, nonBillable, total: billable + nonBillable };
  }, [events]);

  

const handleEventDrop = useCallback(({ event, start, end, allDay }) => {
  setIsDragging(false);
  setModalPosition(getModalPositionStyle(start, currentView));
  setOriginalEventBeforeEdit(prev => prev ?? event);
  
  // Check if the new duration would exceed 23 hours
  const durationHours = (new Date(end) - new Date(start)) / (1000 * 60 * 60);
  if (durationHours > 23) {
    showError('Duration Limit', 'Events cannot exceed 23 hours.');
    return;
  }
  
  // If it's a marked (temporary) event, remove it and create a new marked event at the new spot
  const isMarked = event.approval_status === 'Marked' || (event.logId && event.logId.startsWith('temp-'));
    if (isMarked) {
      setEvents(prev => prev.filter(ev => ev.logId !== event.logId));
      const tempId = `temp-${Date.now()}`;
      const placeholder = {
        title: '',
        start,
        end,
        allDay,
        logId: tempId,
        approval_status: 'Marked',
        projectId: '',
        timecodeId: '',
        billingtype: '',
        projectName: '',
        timecodeName: '',
      };
      setEvents(prev => [...prev, placeholder]);
      setSlotInfo({ start, end, tempId });
      setEditingEvent(null);
      setShowModal(true);
      setOriginalEventBeforeEdit(null);
      return;
    }
    if (event.approval_status !== 'Marked') {
      setEditingEvent(event);
      // Remove marked events if selected is not marked
      setEvents((prev) => prev.filter(ev => ev.approval_status !== 'Marked'));
    }
    // Normal event: open modal for editing
    const updated = { ...event, start, end, allDay, approval_status: 'Changed' };
    setEditingEvent(updated);
    setShowModal(true);
    setEvents((prev) =>
      prev.map((ev) => (ev === event ? updated : ev))
    );
  }, [currentView]);

  const handleEventResize = useCallback(({ event, start, end }) => {
    setIsDragging(false);
    setOriginalEventBeforeEdit(prev => prev ?? event);
    
    // Check if the new duration would exceed 23 hours
    const durationHours = (new Date(end) - new Date(start)) / (1000 * 60 * 60);
    if (durationHours > 23) {
      showError('Duration Limit', 'Events cannot exceed 23 hours.');
      return;
    }
    
    // If it's a marked (temporary) event, remove it and create a new marked event at the new spot
    const isMarked = event.approval_status === 'Marked' || (event.logId && event.logId.startsWith('temp-'));
    if (isMarked) {
      setEvents(prev => prev.filter(ev => ev.logId !== event.logId));
      const tempId = `temp-${Date.now()}`;
      const placeholder = {
        title: '',
        start,
        end,
        logId: tempId,
        approval_status: 'Marked',
        projectId: '',
        timecodeId: '',
        billingtype: '',
        projectName: '',
        timecodeName: '',
      };
      setEvents(prev => [...prev, placeholder]);
      setSlotInfo({ start, end, tempId });
      setEditingEvent(null);
      setShowModal(true);
      setOriginalEventBeforeEdit(null);
      return;
    }

    if (event.approval_status !== 'Marked') {
      setEditingEvent(event);
      // Remove marked events if selected is not marked
      setEvents((prev) => prev.filter(ev => ev.approval_status !== 'Marked'));
    }
    // Normal event: open modal for editing
    const updated = { ...event, start, end, approval_status: 'Changed' };
    //setOriginalEventBeforeEdit(event);
    setEditingEvent(updated);
    setShowModal(true);
    setEvents((prev) =>
      prev.map((ev) => (ev === event ? updated : ev))
    );
  }, []);



  const handleSaveEvent = useCallback((newEvent) => {
    const zuid = new URLSearchParams(window.location.search).get('zuid');

    fetch(`${process.env.REACT_APP_API_BASE_URL}/send-timelog`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        zuid: zuid,
        start: newEvent.start,
        end: newEvent.end,
        projectId: newEvent.projectId,
        timecodeId: newEvent.timecodeId,
        notes: newEvent.title,
        bill_status: newEvent.billingtype,
        date: newEvent.start.toISOString().split('T')[0],
        sendToCapLegal: newEvent.sendToCapLegal,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          console.log('Event saved successfully:', data);

          const newEventWithId = {
            title: newEvent.title,
            start: newEvent.start,
            end: newEvent.end,
            projectId: newEvent.projectId,
            timecodeId: newEvent.timecodeId,
            projectName: newEvent.projectName || '',
            timecodeName: newEvent.timecodeName || '',
            billingtype: newEvent.billingtype || '',
            approval_status: 'Pending',
            logId: data.data.id,
          };

          setEvents((prev) => [...prev, newEventWithId]);

          // ✅ Refetch projects
          fetchProjects();
        } else {
          showError('Save Failed', 'Failed to save event. Please try again.');
        }
      })
      .catch(() => {
        showError('Connection Error', 'Server error while saving event. Please check your connection and try again.');
      });
  }, [fetchProjects, showError]);



  const handleUpdateEvent = useCallback((updatedEvent) => {
    const zuid = new URLSearchParams(window.location.search).get('zuid');

    fetch(`${process.env.REACT_APP_API_BASE_URL}/update-timelog`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        zuid: zuid,
        start: updatedEvent.start,
        end: updatedEvent.end,
        projectId: updatedEvent.projectId,
        timecodeId: updatedEvent.timecodeId,
        notes: updatedEvent.title,
        bill_status: updatedEvent.billingtype,
        date: updatedEvent.start.toISOString().split('T')[0],
        logId: updatedEvent.logId,
        sendToCapLegal: updatedEvent.sendToCapLegal,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setEvents((prev) =>
            prev.map((ev) =>
              ev.logId === updatedEvent.logId ? { ...ev, ...updatedEvent } : ev
            )
          );
          setOriginalEventBeforeEdit(null);
        } else {
          showError('Update Failed', 'Failed to update event. Please try again.');
        }
      })
      .catch(() => {
        showError('Connection Error', 'Server error while updating event. Please check your connection and try again.');
      });
  }, [showError]);

  const handleDeleteEvent = useCallback((logId, projectId, timecodeId) => {
    const zuid = new URLSearchParams(window.location.search).get('zuid');

    fetch(`${process.env.REACT_APP_API_BASE_URL}/delete-timelog`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ zuid, logId, projectId, timecodeId }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setEvents((prev) => prev.filter((ev) => ev.logId !== logId));
        } else {
          showError('Delete Failed', 'Failed to delete event. Please try again.');
        }
      })
      .catch(() => {
        showError('Connection Error', 'Server error while deleting event. Please check your connection and try again.');
      });
  }, [showError]);


  const handleNavigate = useCallback((newDate) => {
    setCurrentDate(newDate);
  }, []);

  const max = useMemo(() => new Date(2025, 4, 10, 23, 59), []);
  const timeslots = 2; // Ændret fra 4 til 2 for 30 minutters slots
  const views = {
    month: true,
    week: true,
    work_week: true,
    three_day: ThreeDayView,
    day: true,
    list: true
  };


  // Handler for day click in month view - navigates to day view
  const handleDayClick = useCallback((date) => {
    setCurrentDate(date);
    setCurrentView('day');
  }, []);

  const handleSelectSlot = useCallback((slotInfo, e) => {
    // In month view, clicking on a date should navigate to day view
    if (currentView === 'month') {
      handleDayClick(slotInfo.start);
      return;
    }

    // Check if this is a right-click
    if (e && e.type === 'contextmenu') {
      handleRightClickSlot(slotInfo, e);
      return;
    }

    // If modal is already open, don't create a new event
    if (showModal) {
      return;
    }

    // Restore previous event if editing and changes were not saved
    if (originalEventBeforeEdit) {
      setEvents(prev =>
        prev.map(ev =>
          ev.logId === originalEventBeforeEdit.logId ? originalEventBeforeEdit : ev
        )
      );
      setOriginalEventBeforeEdit(null);
    }

    setEditingEvent(null);
    setIsDragging(false);
    setSelectedSlotDate(slotInfo.start);
    setModalPosition(getModalPositionStyle(slotInfo.start, currentView));

    if (eventToCopy) {
      const duration = moment(eventToCopy.end).diff(moment(eventToCopy.start));
      const newEnd = moment(slotInfo.start).add(duration, 'ms').toDate();
      const copiedEvent = {
        ...eventToCopy,
        start: slotInfo.start,
        end: newEnd,
      };
      handleSaveEvent(copiedEvent);
      setEventToCopy(null);
    } else {
      const tempId = `temp-${Date.now()}`;
      const placeholder = {
        title: '',
        start: slotInfo.start,
        end: slotInfo.end,
        logId: tempId,
        approval_status: 'Marked',
        projectId: '',
        timecodeId: '',
        billingtype: '',
        projectName: '',
        timecodeName: '',
      };
      setEvents(prev => [
        ...prev.filter(ev => ev.approval_status !== 'Marked' && !(ev.logId && ev.logId.startsWith('temp-'))),
        placeholder
      ]);
      setSlotInfo({ start: slotInfo.start, end: slotInfo.end, tempId });
      setShowModal(true);
    }
  }, [eventToCopy, handleSaveEvent, currentView, originalEventBeforeEdit, handleDayClick, handleRightClickSlot, showModal, slotInfo]);



  const handleSelectEvent = useCallback((event) => {
    // Restore previous event if editing and changes were not saved
    if (originalEventBeforeEdit) {
      setEvents(prev =>
        prev.map(ev =>
          ev.logId === originalEventBeforeEdit.logId ? originalEventBeforeEdit : ev
        )
      );
      setOriginalEventBeforeEdit(null);
    }

    setIsDragging(false);
    setShowDeleteConfirm(false); 
    setEventPendingDelete(null); 
    setOriginalEventBeforeEdit(event);

    // Only set editing event if it's not a marked event
    if (event.approval_status !== 'Marked') {
      setEditingEvent(event);
      // Remove marked events if selected is not marked
      setEvents((prev) => prev.filter(ev => ev.approval_status !== 'Marked'));
    }
    setShowModal(true);
  }, [originalEventBeforeEdit]);

  const handleModalTimeChange = useCallback((updatedEvent) => {
    setEvents((prevEvents) =>
      prevEvents.map(ev =>
        // For editing: match by logId, for marked: match by tempId or start/end
        (updatedEvent.logId && ev.logId === updatedEvent.logId) ||
        (!updatedEvent.logId && slotInfo?.tempId && ev.logId === slotInfo.tempId)
          ? { ...ev, start: updatedEvent.start, end: updatedEvent.end }
          : ev
      )
    );
    // Also update editingEvent if present
    setEditingEvent((prev) =>
      prev && prev.logId === updatedEvent.logId
        ? { ...prev, start: updatedEvent.start, end: updatedEvent.end }
        : prev
    );
    // Also update slotInfo for marked events
    if (!updatedEvent.logId && slotInfo) {
      setSlotInfo((prev) => prev ? { ...prev, start: updatedEvent.start, end: updatedEvent.end } : prev);
    }
    
    // 🚀 Auto-scroll calendar to follow the event when moved with arrow keys
    if (updatedEvent.start && (currentView === 'day' || currentView === 'three_day' || currentView === 'week' || currentView === 'work_week')) {
      // Use smooth scrolling for better UX
      scrollToTime(updatedEvent.start, true);
    }
  }, [slotInfo, currentView, scrollToTime]);
  

  if (error && (error.includes('Error 1') || error.includes('Error 2') || error.includes('Error 3'))) {
    let title, message, details;
    
    if (error.includes('Error 1')) {
      title = 'Authentication Required';
      message = 'Unable to verify your access credentials.';
      details = 'The authentication parameter is missing from your URL.';
    } else if (error.includes('Error 2')) {
      title = 'Login Failed';
      message = 'Authentication could not be completed.';
      details = 'Your login credentials could not be verified with the server.';
    } else if (error.includes('Error 3')) {
      title = 'Connection Error';
      message = 'Unable to connect to the authentication server.';
      details = 'Please check your internet connection and try again.';
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.924-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
            <p className="text-gray-600 mb-4">{message}</p>
            <p className="text-sm text-gray-500 mb-6">{details}</p>
            <div className="bg-[#d6c08b] rounded-lg p-4">
              <p className="text-sm text-white">
                If the issue persists, please contact the IT department.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return <div style={{ padding: '2rem' }}>Authenticating...</div>;
  }

  const formats = {
    timeGutterFormat: (date, culture, localizer) =>
      localizer.format(date, 'HH:mm', culture),

    eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
      `${localizer.format(start, 'HH:mm', culture)} - ${localizer.format(end, 'HH:mm', culture)}`,

    selectRangeFormat: ({ start, end }, culture, localizer) =>
      `${localizer.format(start, 'HH:mm', culture)} – ${localizer.format(end, 'HH:mm', culture)}`,

    agendaTimeFormat: (date, culture, localizer) =>
      localizer.format(date, 'HH:mm', culture),
  };

  

  return (
    <div ref={calendarRef} className="h-screen flex flex-col bg-white">
      {/* Responsive Navigation */}
      <MobileNavigation
        currentView={currentView}
        setCurrentView={setCurrentView}
        currentDate={currentDate}
        handleNavigate={handleNavigate}
        onAddNew={handleAddNew}
        onCreateSeries={handleCreateSeriesFromHeader}
      />

      {/* Calendar Container */}
      <div 
        className="flex-1 h-mobile-calendar md:h-tablet-calendar lg:h-desktop-calendar"
        onContextMenu={(e) => {
          // Check if the right-click was on a calendar slot (not on an event)
          const target = e.target;
          const isEvent = target.closest('.rbc-event');
          const isSlot = target.closest('.rbc-time-slot') || target.closest('.rbc-day-slot');
          
          if (!isEvent && isSlot && currentView !== 'month') {
                           // Calculate the actual slot time from mouse position
               // Use a much simpler approach - just calculate from pixel position
               const calendarRect = e.currentTarget.getBoundingClientRect();
               const timeColumn = target.closest('.rbc-time-column') || target.closest('.rbc-day-slot');
               
               if (timeColumn) {
                 const columnRect = timeColumn.getBoundingClientRect();
                 const mouseY = e.clientY - columnRect.top;
                 
                 // Get the time gutter to understand the time scale
                 const timeView = e.currentTarget.querySelector('.rbc-time-view');
                 const timeGutter = timeView ? timeView.querySelector('.rbc-time-gutter') : null;
                 
                 let startHour = 0; // Default start hour (midnight)
                 let endHour = 24; // Default end hour  
                 let totalHours = 24; // Default hours shown (full day)
                 
                 if (timeGutter) {
                   // Try to get actual start/end from time labels
                   const timeLabels = timeGutter.querySelectorAll('.rbc-time-slot');
                   if (timeLabels.length > 0) {
                     // Get first time
                     const firstTimeText = timeLabels[0].textContent?.trim();
                     if (firstTimeText && firstTimeText.includes(':')) {
                       const match = firstTimeText.match(/(\d+):/);
                       if (match) {
                         startHour = parseInt(match[1]);
                       }
                     }
                     
                     // Get last time  
                     const lastTimeText = timeLabels[timeLabels.length - 1].textContent?.trim();
                     if (lastTimeText && lastTimeText.includes(':')) {
                       const match = lastTimeText.match(/(\d+):/);
                       if (match) {
                         endHour = parseInt(match[1]) + 1; // Add 1 to get end hour
                       }
                     }
                     
                     totalHours = endHour - startHour;
                     console.log(`Calendar time range: ${startHour}:00 - ${endHour}:00 (${totalHours} hours)`);
                   }
                 }
                 
                 // Simple pixel-based calculation
                                const percentDown = mouseY / columnRect.height;
               const hoursFromStart = percentDown * totalHours;
               const totalMinutesFromStart = Math.floor(hoursFromStart * 60);
               
               // Round to nearest 30-minute interval (hel/halv timer)
               const roundedMinutes = Math.round(totalMinutesFromStart / 30) * 30;
                 const finalHour = startHour + Math.floor(roundedMinutes / 60);
                 const finalMinute = roundedMinutes % 60;
                 
                 console.log(`Simple calculation: mouseY=${mouseY}, height=${columnRect.height}, percent=${percentDown.toFixed(3)}, hours=${hoursFromStart.toFixed(2)} → ${finalHour}:${finalMinute.toString().padStart(2, '0')}`);
               
               // Create the slot start time for today (or current date in view)
               const slotStart = new Date(currentDate);
               slotStart.setHours(finalHour, finalMinute, 0, 0);
              
              // If we're in day view and it's a different day, use that day
              if (currentView === 'day' || currentView === 'three_day') {
                // Use the date from the day/three_day view
                const dayDate = new Date(currentDate);
                slotStart.setFullYear(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
                             } else if (currentView === 'week' || currentView === 'work_week') {
                 // For week views, we need to determine which day column was clicked
                 // Find the correct day column selector - react-big-calendar uses different classes
                 const timeView = e.currentTarget.querySelector('.rbc-time-view');
                 const dayColumns = timeView ? timeView.querySelectorAll('.rbc-time-column') : [];
                 let dayIndex = 0;
                 
                 // Alternative approach: find all day header elements to map positions
                 const headers = e.currentTarget.querySelectorAll('.rbc-header');
                 
                 if (headers.length > 0) {
                   // Use headers to determine day positions
                   for (let i = 0; i < headers.length; i++) {
                     const headerRect = headers[i].getBoundingClientRect();
                     if (e.clientX >= headerRect.left && e.clientX <= headerRect.right) {
                       dayIndex = i;
                       console.log(`Slot clicked: Header ${i} (${headers[i].textContent?.trim()})`);
                       break;
                     }
                   }
                 } else if (dayColumns.length > 0) {
                   // Fallback to time columns
                   for (let i = 0; i < dayColumns.length; i++) {
                     const dayRect = dayColumns[i].getBoundingClientRect();
                     if (e.clientX >= dayRect.left && e.clientX <= dayRect.right) {
                       dayIndex = i;
                       console.log(`Slot clicked: Column ${i}`);
                       break;
                     }
                   }
                 }
                 
                 // Calculate the date for this day column
                 // Always use isoWeek (Monday start) to match calendar display
                 let startOfWeek = moment(currentDate).startOf('isoWeek');
                 
                 const dayDate = startOfWeek.clone().add(dayIndex, 'days').toDate();
                 slotStart.setFullYear(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
                 
                 console.log(`Slot calculated: ${moment(slotStart).format('dddd YYYY-MM-DD HH:mm')}`);
               }
              
              const slotEnd = new Date(slotStart.getTime() + 60 * 60 * 1000); // +1 hour
              
              const slotInfo = {
                start: slotStart,
                end: slotEnd
              };
              
              handleRightClickSlot(slotInfo, e);
            }
          }
        }}
        {...setupLongPress((e, syntheticEvent) => {
          // Reuse the same logic as onContextMenu for slots
          const target = syntheticEvent.target;
          const isEvent = target.closest('.rbc-event');
          const isSlot = target.closest('.rbc-time-slot') || target.closest('.rbc-day-slot');
          
          if (!isEvent && isSlot && currentView !== 'month') {
            // Calculate slot time and handle long press like right-click
            //const calendarRect = syntheticEvent.currentTarget.getBoundingClientRect();
            const timeColumn = target.closest('.rbc-time-column') || target.closest('.rbc-day-slot');
            
            if (timeColumn) {
              const columnRect = timeColumn.getBoundingClientRect();
              const mouseY = syntheticEvent.clientY - columnRect.top;
              
              const timeView = syntheticEvent.currentTarget.querySelector('.rbc-time-view');
              const timeGutter = timeView ? timeView.querySelector('.rbc-time-gutter') : null;
              
              let startHour = 0;
              let endHour = 24;
              let totalHours = 24;
              
              if (timeGutter) {
                const timeLabels = timeGutter.querySelectorAll('.rbc-time-slot');
                if (timeLabels.length > 0) {
                  const firstTimeText = timeLabels[0].textContent?.trim();
                  if (firstTimeText && firstTimeText.includes(':')) {
                    const match = firstTimeText.match(/(\d+):/);
                    if (match) {
                      startHour = parseInt(match[1]);
                    }
                  }
                  
                  const lastTimeText = timeLabels[timeLabels.length - 1].textContent?.trim();
                  if (lastTimeText && lastTimeText.includes(':')) {
                    const match = lastTimeText.match(/(\d+):/);
                    if (match) {
                      endHour = parseInt(match[1]) + 1;
                    }
                  }
                  
                  totalHours = endHour - startHour;
                }
              }
              
              const percentDown = mouseY / columnRect.height;
              const hoursFromStart = percentDown * totalHours;
              const totalMinutesFromStart = Math.floor(hoursFromStart * 60);
              const roundedMinutes = Math.round(totalMinutesFromStart / 30) * 30; // Ændret fra 15 til 30 for hel/halv timer
              const finalHour = startHour + Math.floor(roundedMinutes / 60);
              const finalMinute = roundedMinutes % 60;
              
              const slotStart = new Date(currentDate);
              slotStart.setHours(finalHour, finalMinute, 0, 0);
              
              if (currentView === 'day' || currentView === 'three_day') {
                const dayDate = new Date(currentDate);
                slotStart.setFullYear(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
              } else if (currentView === 'week' || currentView === 'work_week') {
                const timeView = syntheticEvent.currentTarget.querySelector('.rbc-time-view');
                const headers = syntheticEvent.currentTarget.querySelectorAll('.rbc-header');
                let dayIndex = 0;
                
                if (headers.length > 0) {
                  for (let i = 0; i < headers.length; i++) {
                    const headerRect = headers[i].getBoundingClientRect();
                    if (syntheticEvent.clientX >= headerRect.left && syntheticEvent.clientX <= headerRect.right) {
                      dayIndex = i;
                      break;
                    }
                  }
                }
                
                let startOfWeek = moment(currentDate).startOf('isoWeek');
                const dayDate = startOfWeek.clone().add(dayIndex, 'days').toDate();
                slotStart.setFullYear(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
              }
              
              const slotEnd = new Date(slotStart.getTime() + 60 * 60 * 1000);
              const slotInfo = {
                start: slotStart,
                end: slotEnd
              };
              
              handleRightClickSlot(slotInfo, syntheticEvent);
            }
          }
        })}
      >
{currentView === 'list' ? (
          <ListView
            events={events}
            currentDate={currentDate}
            onSelectEvent={handleSelectEvent}
            onRightClickEvent={handleRightClickEvent}
            setupLongPress={setupLongPress}
          />
        ) : (
          <DnDCalendar
          onDragStart={() => setIsDragging(true)}
          localizer={localizer}
          events={events}
          date={currentDate}
          onNavigate={handleNavigate}
          startAccessor="start"
          endAccessor="end"
          view={currentView}
          onView={setCurrentView}
          views={views}
          max={max}
          step={30}
          snapToSlot={false}
          timeslots={timeslots}
          showMultiDayTimes
          selectable={currentView !== 'month'}
          popup
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          onEventDrop={handleEventDrop}
          onEventResize={handleEventResize}
          resizable
          formats={formats}
          eventPropGetter={eventPropGetter}
          draggableAccessor={isDraggable}
          resizableAccessor={isResizable}
          components={{
            toolbar: () => null,
            week: {
              header: ({ date, label }) => {
                const dayName = moment(date).format('dd'); // Mo, Tu, etc
                const dayNumber = moment(date).format('D'); // 1, 2, etc
                
                return (
                  <div className="text-center">
                    {/* Mobile optimized header */}
                    <div className="md:hidden">
                      <div className="text-xs font-semibold text-gray-700">{dayName}</div>
                      <div className="text-sm font-bold">{dayNumber}</div>
                    </div>
                    
                    {/* Desktop header */}
                    <div className="hidden md:block">
                      <div className="font-medium">{label}</div>
                      {(currentView === 'week' || currentView === 'work_week' || currentView === 'three_day') && (
                        <div className="text-xs text-gray-600 mt-1 font-medium">
                          Total: {formatHours(getDailyTotals(date).total)} - Billable: {formatHours(getDailyTotals(date).billable)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              },
            },
            work_week: {
              header: ({ date, label }) => {
                const dayName = moment(date).format('dd'); // Mo, Tu, etc
                const dayNumber = moment(date).format('D'); // 1, 2, etc
                
                return (
                  <div className="text-center">
                    {/* Mobile optimized header */}
                    <div className="md:hidden">
                      <div className="text-xs font-semibold text-gray-700">{dayName}</div>
                      <div className="text-sm font-bold">{dayNumber}</div>
                    </div>
                    
                    {/* Desktop header */}
                    <div className="hidden md:block">
                      <div className="font-medium">{label}</div>
                      {(currentView === 'week' || currentView === 'work_week' || currentView === 'three_day') && (
                        <div className="text-xs text-gray-600 mt-1 font-medium">
                          Total: {formatHours(getDailyTotals(date).total)} - Billable: {formatHours(getDailyTotals(date).billable)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              },
            },
            day: {
              header: ({ date, label }) => {
                const dayName = moment(date).format('dddd'); // Monday, Tuesday, etc
                const dayNumber = moment(date).format('D'); // 1, 2, etc
                const monthName = moment(date).format('MMM'); // Jan, Feb, etc
                
                return (
                  <div className="text-center">
                    {/* Mobile optimized header */}
                    <div className="md:hidden">
                      <div className="text-xs font-semibold text-gray-700">{dayName}</div>
                      <div className="text-sm font-bold">{monthName} {dayNumber}</div>
                    </div>
                    
                    {/* Desktop header */}
                    <div className="hidden md:block">
                      <div className="font-medium">{label}</div>
                    </div>
                  </div>
                );
              },
            },
            month: {
              dateCellWrapper: ({ value, children }) => {
                const date = value;
                const dayNumber = moment(date).format('D');
                const dailyTotals = getDailyTotals(date);
                const isToday = moment(date).isSame(moment(), 'day');
                
                return (
                  <div 
                    className={`w-full h-full cursor-pointer p-1 md:p-2 flex flex-col border-l border-gray-300 ${
                      isToday ? 'bg-beige border-1 border-gray-300' : ''
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Day clicked:', dayNumber); // Debug
                      handleDayClick(date);
                    }}
                    title="Click to view day"
                    style={{ 
                      minHeight: '100%',
                      position: 'relative',
                      zIndex: 12
                    }}
                  >
                    {/* Skjul original children da vi viser vores eget indhold */}
                    <div style={{ display: 'none' }}>{children}</div>
                    
                    {/* Vores custom indhold fylder hele cellen */}
                    <div className="flex-1 flex flex-col justify-center items-center text-center">
                      <div className={`text-sm md:text-lg font-bold mb-0.5 md:mb-1 ${
                        isToday ? '' : ''
                      }`}>
                        {dayNumber}                        
                      </div>
                      <div className="text-xs space-y-0 md:space-y-0.5">
                        <div className="font-medium text-gray-600">
                          {/* Mobile: forkortet labels, Desktop: fulde labels */}
                          <span className="md:hidden">T: {formatHours(dailyTotals.total)}</span>
                          <span className="hidden md:inline">Total: {formatHours(dailyTotals.total)}</span>
                        </div>
                        <div className="font-medium text-gray-600">
                          <span className="md:hidden">B: {formatHours(dailyTotals.billable)}</span>
                          <span className="hidden md:inline font-bold">Billable: {formatHours(dailyTotals.billable)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              },
            },
            event: ({ event }) => {
              // Don't render events in month view - only show date and hours
              if (currentView === 'month') {
                return null;
              }
              
              // Calculate event duration in minutes
              const durationMinutes = moment(event.end).diff(moment(event.start), 'minutes');
              const isShortEvent = durationMinutes <= 15;
              
              if (isShortEvent) {
                // Compact layout for short events (≤15 minutes)
                const startTime = moment(event.start).format('HH:mm');
                const endTime = moment(event.end).format('HH:mm');
                
                // Hide rbc-event-label using direct DOM manipulation
                setTimeout(() => {
                  const allEvents = document.querySelectorAll('.rbc-event');
                  allEvents.forEach(eventEl => {
                    const customDiv = eventEl.querySelector('[data-short-event="true"]');
                    if (customDiv) {
                      const label = eventEl.querySelector('.rbc-event-label');
                      if (label) {
                        label.style.display = 'none';
                        label.style.visibility = 'hidden';
                        label.style.opacity = '0';
                      }
                      // Also set attribute on parent
                      eventEl.setAttribute('data-short-event', 'true');
                    }
                  });
                }, 0);
                
                return (
                  <div
                    onContextMenu={(e) => handleRightClickEvent(event, e)}
                    {...setupLongPress((e, syntheticEvent) => handleRightClickEvent(event, syntheticEvent))}
                    style={{ 
                      height: '100%',
                      position: 'relative',
                      zIndex: 12
                    }}
                    className="flex items-center justify-between w-full h-full px-2 py-1 min-h-[24px] cursor-pointer"
                    data-short-event="true"
                  >
                    <div className="flex items-center min-w-0 flex-1">
                      <span className="text-[9px] font-medium text-gray-600 whitespace-nowrap mr-1">
                        {startTime}-{endTime}
                      </span>
                      <span className="text-xs font-semibold truncate">
                        {event.title.length > 20 ? `${event.title.slice(0, 20)}...` : event.title}
                      </span>
                    </div>
                    <img 
                      src={
                        event.billingtype === 'Billable' 
                          ? '/stateIcons/money.png'
                          : event.billingtype === 'Pro Bono'
                          ? '/stateIcons/sustainability.png'
                          : '/stateIcons/workplace.png'
                      }
                      alt={event.billingtype}
                      className="w-4 h-4 flex-shrink-0 ml-2"
                    />
                  </div>
                );
              } else {
                // Normal layout for longer events (>15 minutes)
                return (
                  <div
                    onContextMenu={(e) => handleRightClickEvent(event, e)}
                    {...setupLongPress((e, syntheticEvent) => handleRightClickEvent(event, syntheticEvent))}
                    style={{ height: '100%' }}
                    className="flex flex-col h-full"
                  >
                    <div className="flex items-center mb-1 justify-between w-full">
                      <strong className="text-[13px] font-bold leading-tight">
                        {event.title.length > 35 ? `${event.title.slice(0, 35)}...` : event.title}
                      </strong>
                      <img 
                        src={
                          event.billingtype === 'Billable' 
                            ? '/stateIcons/money.png'
                            : event.billingtype === 'Pro Bono'
                            ? '/stateIcons/sustainability.png'
                            : '/stateIcons/workplace.png'
                        }
                        alt={event.billingtype}
                        className="w-6 h-6 inline-block ml-1"
                      />
                    </div>
                    <small className="text-[11px] leading-tight text-gray-600">{event.timecodeName}</small>
                    <small className="text-[11px] leading-tight text-gray-600">{event.projectName}</small>
                  </div>
                );
              }
            },
          }}
        />
        )}
      </div>

      {/* Responsive Stats Footer - hidden in ListView */}
      {currentView !== 'list' && (
        <MobileFooter
          totalsCurrentView={totalsCurrentView}
          totalsYTD={totalsYTD}
          currentView={currentView}
          currentDate={currentDate}
          customRangeData={customRangeData}
          isLoadingCustomRange={isLoadingCustomRange}
          onCustomDateRangeClick={handleCustomDateRangeClick}
          onClearCustomRange={handleClearCustomRange}
        />
      )}

      {/* Context Menu for Events */}
      {contextMenu.visible && (
        <ul
          ref={contextMenuRef}
          className="absolute bg-white border border-gray-200 shadow-lg rounded-md p-2 z-50"
          style={{
            top: contextMenu.y,
            left: contextMenu.x,
          }}
        >
          {/* Only show Copy if not Outlook synced event */}
          {contextMenu.event?.projectId !== '274231000012917617' && (
            <li
              className="px-2 py-1 cursor-pointer hover:bg-gray-100"
              onClick={() => {
                setEventToCopy(contextMenu.event);
                setContextMenu({ visible: false, x: 0, y: 0, event: null });
              }}
            >
              Copy
            </li>
          )}
          <li
            className="px-2 py-1 cursor-pointer hover:bg-gray-100"
            onClick={() => {
              handleCreateSeries(contextMenu.event);
            }}
          >
            Create Series
          </li>
          {/* Show Delete Entire Series if this event is part of a series */}
          {contextMenu.event?.seriesId && (
            <li
              className="px-2 py-1 cursor-pointer text-orange-600 hover:bg-gray-100"
              onClick={() => {
                handleDeleteSeries(contextMenu.event.seriesId);
              }}
            >
              Delete Entire Series
            </li>
          )}
          {/* Only show Edit and Delete if not Approved */}
          {contextMenu.event?.approval_status !== 'Approved' && (
            <>
              <li
                className="px-2 py-1 cursor-pointer hover:bg-gray-100"
                onClick={() => {
                  setEditingEvent(contextMenu.event);
                  setShowModal(true);
                  setContextMenu({ visible: false, x: 0, y: 0, event: null });
                }}
              >
                {contextMenu.event?.projectId === '274231000012917617' ? 'Convert' : 'Edit'}
              </li>
              <li
                className="px-2 py-1 cursor-pointer text-red-600 hover:bg-gray-100"
                onClick={() => {
                  setEventPendingDelete(contextMenu.event);
                  setShowDeleteConfirm(true);
                  setContextMenu({ visible: false, x: 0, y: 0, event: null });
                }}
              >
                Delete
              </li>
            </>
          )}
        </ul>
      )}

      {/* Context Menu for Empty Slots */}
      {slotContextMenu.visible && (
        <ul
          ref={slotContextMenuRef}
          className="absolute bg-white border border-gray-200 shadow-lg rounded-md p-2 z-50"
          style={{
            top: slotContextMenu.y,
            left: slotContextMenu.x,
          }}
        >
          <li
            className="px-2 py-1 cursor-pointer hover:bg-gray-100"
            onClick={() => {
              // Create new event at this slot
              const slotInfo = slotContextMenu.slot;
              const tempId = `temp-${Date.now()}`;
              const placeholder = {
                title: '',
                start: slotInfo.start,
                end: slotInfo.end,
                logId: tempId,
                approval_status: 'Marked',
                projectId: '',
                timecodeId: '',
                billingtype: '',
                projectName: '',
                timecodeName: '',
              };
              setEvents(prev => [
                ...prev.filter(ev => ev.approval_status !== 'Marked' && !(ev.logId && ev.logId.startsWith('temp-'))),
                placeholder
              ]);
              setSlotInfo({ start: slotInfo.start, end: slotInfo.end, tempId });
              setModalPosition(getModalPositionStyle(slotInfo.start, currentView));
              setShowModal(true);
              setSlotContextMenu({ visible: false, x: 0, y: 0, slot: null });
            }}
          >
            Add New
          </li>
                     {eventToCopy && (
             <li
               className="px-2 py-1 cursor-pointer hover:bg-gray-100"
               onClick={() => {
                 // Paste copied event at this slot
                 const slotInfo = slotContextMenu.slot;
                 const duration = moment(eventToCopy.end).diff(moment(eventToCopy.start));
                 const newEnd = moment(slotInfo.start).add(duration, 'ms').toDate();
                 const copiedEvent = {
                   ...eventToCopy,
                   start: slotInfo.start,
                   end: newEnd,
                 };
                 
                 console.log('Paste event debug:', {
                   originalEvent: {
                     start: moment(eventToCopy.start).format('YYYY-MM-DD dddd HH:mm'),
                     end: moment(eventToCopy.end).format('YYYY-MM-DD dddd HH:mm')
                   },
                   slotInfo: {
                     start: moment(slotInfo.start).format('YYYY-MM-DD dddd HH:mm'),
                     end: moment(slotInfo.end).format('YYYY-MM-DD dddd HH:mm')
                   },
                   copiedEvent: {
                     start: moment(copiedEvent.start).format('YYYY-MM-DD dddd HH:mm'),
                     end: moment(copiedEvent.end).format('YYYY-MM-DD dddd HH:mm')
                   }
                 });
                 
                 handleSaveEvent(copiedEvent);
                 setEventToCopy(null);
                 setSlotContextMenu({ visible: false, x: 0, y: 0, slot: null });
               }}
             >
               Paste
             </li>
           )}
        </ul>
      )}

      {!isDragging && (
        <EventFormModal
        onTimeChange={handleModalTimeChange}
        onNavigate={handleNavigate}
        currentDate={currentDate}
        key={editingEvent?.logId || slotInfo?.tempId || 'new'}
        modalPosition={modalPosition}
        setModalPosition={setModalPosition}
        getModalPositionStyle={getModalPositionStyle}
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingEvent(null);
          if (slotInfo?.tempId) {
            setEvents(prev => prev.filter(ev => ev.logId !== slotInfo.tempId));
          }
          setSlotInfo(null);
          // Restore original event if editing and changes were not saved
          if (originalEventBeforeEdit) {
            setEvents(prev =>
              prev.map(ev =>
                ev.logId === originalEventBeforeEdit.logId ? originalEventBeforeEdit : ev
              )
            );
            setOriginalEventBeforeEdit(null);
          }
        }}
        onSave={async (eventData) => {
          // Only for editing, not new
          if (
            editingEvent &&
            editingEvent.projectId === '274231000012917617' &&
            eventData.projectId !== editingEvent.projectId
          ) {
            // Delete old, then create new
            await handleDeleteEvent(editingEvent.logId, editingEvent.projectId, editingEvent.timecodeId);
            handleSaveEvent(eventData);
          } else if (editingEvent) {
            handleUpdateEvent({ ...eventData, logId: editingEvent.logId });
          } else {
            handleSaveEvent(eventData);
          }
          setShowModal(false);
          setEditingEvent(null);
          setSlotInfo(null);
          setOriginalEventBeforeEdit(null);
        }}
        onDelete={handleDeleteEvent}
        start={editingEvent?.start || slotInfo?.start}
        end={editingEvent?.end || slotInfo?.end}
        projects={projects}
        initialData={editingEvent}
        selectedSlotDate={selectedSlotDate}
        currentView={currentView}        
      />
      )}
      <ConfirmModal 
        isOpen={showDeleteConfirm}
        onConfirm={() => {
          if (eventPendingDelete) {
            handleDeleteEvent(eventPendingDelete.logId, eventPendingDelete.projectId, eventPendingDelete.timecodeId);
            setEventPendingDelete(null);
            setShowDeleteConfirm(false);
          }
        }}
        onCancel={() => {
          setEventPendingDelete(null);
          setShowDeleteConfirm(false);
        }}
        title="Delete Registration"
        message="Are you sure you want to delete this time registration?"
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
      
      {/* Series Delete Confirmation Modal */}
      <ConfirmModal 
        isOpen={showSeriesDeleteConfirm}
        onConfirm={handleConfirmDeleteSeries}
        onCancel={() => {
          setSeriesPendingDelete({ seriesId: null, events: [] });
          setShowSeriesDeleteConfirm(false);
        }}
        title="Delete Entire Series"
        message={`Are you sure you want to delete ${seriesPendingDelete.events.length} time registration${seriesPendingDelete.events.length !== 1 ? 's' : ''} from this series? This action cannot be undone.`}
        confirmText={`Delete ${seriesPendingDelete.events.length} Registration${seriesPendingDelete.events.length !== 1 ? 's' : ''}`}
        confirmButtonClass="bg-orange-600 hover:bg-orange-700"
      />
      
      {/* Series Deletion Progress Overlay */}
      {isDeletingSeries && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <div 
                  className="animate-spin rounded-full h-8 w-8 border-b-2 mr-3"
                  style={{ borderColor: '#ccb778' }}
                ></div>
                <div className="text-lg font-semibold text-gray-900">
                  Deleting Series
                </div>
              </div>
              <div className="text-sm text-gray-600 mb-4">
                Deleting {deleteProgress.current} of {deleteProgress.total} registrations...
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="h-3 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${(deleteProgress.current / deleteProgress.total) * 100}%`,
                    backgroundColor: '#ccb778'
                  }}
                ></div>
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Please wait while we delete all entries...
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* 🛡️ Series Recovery Modal */}
      {showRecoveryModal && recoveryData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="text-center">
              <div className="text-lg font-semibold text-gray-900 mb-4">
                📋 Incomplete Series Found
              </div>
              <div className="text-sm text-gray-600 mb-4">
                Found an incomplete series creation from {recoveryData.hoursElapsed < 1 ? 
                  `${Math.round(recoveryData.hoursElapsed * 60)} minutes` : 
                  `${recoveryData.hoursElapsed} hours`} ago:
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-4">
                <div className="text-sm">
                  <strong>Series:</strong> {recoveryData.templateData.title}<br/>
                  <strong>Created:</strong> {recoveryData.createdEvents.length} of {recoveryData.totalEvents} events<br/>
                  <strong>Project:</strong> {projects.find(p => p.id === recoveryData.templateData.projectId)?.name || 'Unknown'}
                </div>
              </div>
              <div className="text-sm text-gray-600 mb-6">
                What would you like to do with the already created time registrations?
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleRecoveryCleanup}
                  disabled={isRecovering}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 text-sm"
                >
                  {isRecovering ? 'Deleting...' : '🗑️ Delete All'}
                </button>
                <button
                  onClick={handleRecoveryDismiss}
                  disabled={isRecovering}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 text-sm"
                >
                  ✅ Keep Events
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={errorModalData.title}
        message={errorModalData.message}
      />
      
      {/* Series Modal */}
      <SeriesModal
        isOpen={showSeriesModal}
        onClose={() => {
          setShowSeriesModal(false);
          setSeriesTemplateEvent(null);
        }}
        onCreateSeries={handleBatchCreateSeries}
        templateEvent={seriesTemplateEvent}
        projects={projects}
      />

      {/* Custom Date Range Modal */}
      <CustomDateRangeModal
        isOpen={showCustomDateModal}
        onClose={handleCustomDateRangeClose}
        onApply={handleCustomDateRangeApply}
        currentRange={customDateRange}
      />
    </div>
  );
}

App.propTypes = {
  localizer: PropTypes.instanceOf(DateLocalizer),
};

export default App;
